import type { CsvTransformerControlsMapType } from "../models";
import { useCsvAvailabilityCellTransformer } from "../use-csv-availability-cell.transformer";
import { useCsvBooleanCellTransformer } from "../use-csv-boolean-cell.transformer";
import { useCsvCourseScheduleCellTransformer } from "../use-csv-course-schedule-cell.transformer";
import { useCsvCourseStationsCellTransformer } from "../use-csv-course-stations-cell.transformer";
import { useCsvDateCellTransformer } from "../use-csv-date-cell.transformer";
import { useCsvDynamicCellTransformer } from "../use-csv-dynamic-cell.transformer";
import { useCsvJsonCellTransformer } from "../use-csv-json-cell.transformer";
import { useCsvListCellTransformer } from "../use-csv-list-cell.transformer";
import { useCsvNumberCellTransformer } from "../use-csv-number-cell.transformer";
import { useCsvProductionChainCellTransformer } from "../use-csv-production-chain-cell.transformer";
import { useCsvRidePassengerListCellTransformer } from "../use-csv-ride-passengers-list-cell.transformer";
import { useCsvRideStartEndDateCellTransformer } from "../use-csv-ride-start-end-date-cell.transformer";
import { useCsvTagsCellTransformer } from "../use-csv-tags-cell.transformer";
import { useCsvTextCellTransformer } from "../use-csv-text-cell.transformer";
import { useCsvTimeCellTransformer } from "../use-csv-time-cell.transformer";

interface IUseCsvTransformerControlsMap {
  csvTransformerControlsMap: CsvTransformerControlsMapType;
}

export const useCsvTransformerControlsMap =
  (): IUseCsvTransformerControlsMap => {
    const { getTextValue } = useCsvTextCellTransformer();
    const { getNumberValue } = useCsvNumberCellTransformer();
    const { getDateValue } = useCsvDateCellTransformer();
    const { getTimeValue } = useCsvTimeCellTransformer();
    const { getBooleanValue } = useCsvBooleanCellTransformer();
    const { getCsvListValue } = useCsvListCellTransformer();
    const { getTagsListValue } = useCsvTagsCellTransformer();
    const { getRidePassengerListValue } =
      useCsvRidePassengerListCellTransformer();
    const { getRideStartEndDateValue } =
      useCsvRideStartEndDateCellTransformer();
    useCsvJsonCellTransformer();
    const { getAvailabilityValue } = useCsvAvailabilityCellTransformer();
    const { getProductionChainValue } = useCsvProductionChainCellTransformer();
    const { getCourseScheduleValue } = useCsvCourseScheduleCellTransformer();
    const { getCourseStationsValue } = useCsvCourseStationsCellTransformer();

    // Dynamic access value transformer function
    // based on the controlInfoName or other properties.
    const { getDynamicTransformerFn } = useCsvDynamicCellTransformer();

    const csvTransformerControlsMap: CsvTransformerControlsMapType = {
      price_rate_pricing: getDynamicTransformerFn,
      address: getDynamicTransformerFn,
      boolean_image_icon: getBooleanValue,
      booking_status: getTextValue,
      feedback_status: getTextValue,
      feedbacks_count: getTextValue,
      ride_passengers_csv_list: getRidePassengerListValue,
      checkbox: getTextValue,
      complaint_status: getTextValue,
      course_schedule: getCourseScheduleValue,
      csv_list: getCsvListValue,
      currency: getTextValue,
      date: getDateValue,
      date_range: getTextValue,
      date_with_indicator: getTextValue,
      datetime: getDateValue,
      datetime_compact_readonly: getDateValue,
      datetime_range: getTextValue,
      direction: getTextValue,
      dropdown_bound_text: getTextValue,
      entity_name: getTextValue,
      email: getTextValue,
      number: getNumberValue,
      phone: getTextValue,
      ride_status: getTextValue,
      switch: getBooleanValue,
      switch_alternative: getBooleanValue,
      switch_confirm: getBooleanValue,
      tags: getTagsListValue,
      text: getTextValue,
      plain_text: getTextValue,
      time: getTimeValue,
      time_range: getTextValue,
      url: getTextValue,
      vehicle_occupancy: getTextValue,
      ride_start_end_datetime: getRideStartEndDateValue,
      action_button: getTextValue,
      notification_priority: getTextValue,
      date_countdown: getTextValue,
      notification_grid_content: getTextValue,
      entity_link: getTextValue,
      open_entity_form: getTextValue,
      ride_editable_dropdown_bound: getTextValue,
      ride_editable_docks_dropdown: getTextValue,
      live_map_arrival_delay: getNumberValue,
      live_map_delay_status: getTextValue,
      live_map_ride_start_status: getTextValue,
      ride_route_completion_status: getTextValue,
      table_availability: getAvailabilityValue,
      production_chain: getProductionChainValue,
      course_stations: getCourseStationsValue,
    };

    return { csvTransformerControlsMap };
  };
